/**
 * Paramètres de recherche discovery (GET /search/pros) et navigation vers /recherche.
 */

import i18n, { isDutchBookingLanguage } from '../config/i18n';
import { DEFAULT_PROFESSIONS, findMatchingProfessionsSimple } from './DefaultProfessions';
import { resolveSpeciesSearchIntent } from './speciesSearchIntent';
import { METIERS_DATA } from '../pages/MetierLanding/metiersData';
import { BOOKING_LANDING_PATH, SEARCH_RESULTS_PATH } from '../config/site';
import {
  discoverySearchIsAskMode,
  discoverySearchHasMinimumFilters,
  discoverySearchHasStructuredFilter,
  discoverySearchShouldRenderPage,
  hasDiscoveryGeo,
  normalizeLegacyDiscoverySearchParams,
} from './discoverySearchUrl';
import { discoveryMfecFilterActive, setDiscoveryMfecFilter } from './discoveryMfecFilter';

export {
  discoverySearchHasMinimumFilters,
  discoverySearchHasStructuredFilter,
  discoverySearchShouldRenderPage,
  hasDiscoveryGeo,
  normalizeLegacyDiscoverySearchParams,
  discoverySearchIsAskMode,
};

/** Libellé FR historique (URL / tests). Préférer getDefaultProfessionLabel() en UI. */
export const DEFAULT_PROFESSION_LABEL = 'Tous les professionnels';

export function getDefaultProfessionLabel(): string {
  return isDutchBookingLanguage(i18n.language)
    ? 'Alle professionals'
    : DEFAULT_PROFESSION_LABEL;
}

export function isDefaultProfessionLabel(value: string | null | undefined): boolean {
  const q = String(value || '').trim();
  return q === DEFAULT_PROFESSION_LABEL || q === 'Alle professionals';
}

/** Libellé lisible pour un slug `profession` présent dans l’URL */
export function labelForProfessionSlug(slug: string): string | undefined {
  return DEFAULT_PROFESSIONS.find(p => p.slug === slug)?.name;
}

const SORT_VALUES = ['relevance', 'distance', 'price'] as const;
export type DiscoverySortParam = (typeof SORT_VALUES)[number];

/** Rayons autorisés pour GET /search/pros (backend min 5 / max 200 km). */
export const DISCOVERY_RADIUS_KM_OPTIONS = [5, 10, 15, 20, 25, 50, 100] as const;
export const DEFAULT_DISCOVERY_RADIUS_KM = 25;
/** Auto-élargissement ask : 25 → 50, puis CTA 100. Jamais de dump national. */
export const ASK_EXPAND_RADIUS_KM = [25, 50, 100] as const;

export interface BuildProsDiscoveryParamsArgs {
  searchQuoi: string;
  /** Renseigné uniquement quand l’utilisateur a cliqué une ligne « métier » (slug API) */
  selectedProfessionSlug: string | null;
  /** Renseigné uniquement quand l’utilisateur a cliqué une ligne « type de service » (service_types.id) */
  selectedServiceTypeId?: string | number | null;
  /** Libellé lisible conservé dans l’URL pour réhydrater le champ recherche */
  selectedServiceTypeName?: string | null;
  /** Espèce animale (species.id) — filtre Booking optionnel */
  selectedSpeciesId?: string | number | null;
  searchOu: string;
  lat: number;
  lon: number;
  /** `location.search` de la page courante (ex. pour conserver `sort`) */
  currentLocationSearch: string;
  /**
   * Pays discovery (Phase G). `be`/`BE` → `country=BE` (filtre API).
   * FR / absent → pas de param (comportement historique FR inchangé).
   */
  country?: string | null;
}

/** Normalise un code pays discovery (FR/BE/NL) ; sinon null. */
export function normalizeDiscoveryCountry(
  raw: string | null | undefined
): 'FR' | 'BE' | 'NL' | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase().slice(0, 2);
  if (code === 'FR' || code === 'BE' || code === 'NL') return code;
  return null;
}

/**
 * Pays à envoyer à GET /search/pros.
 * BE / NL (filtre explicite) ; FR reste sans param pour ne pas changer le rayon historique.
 */
export function discoveryCountryQueryParam(
  geoOrExplicit: string | null | undefined
): 'BE' | 'NL' | null {
  const code = normalizeDiscoveryCountry(geoOrExplicit);
  if (code === 'BE' || code === 'NL') return code;
  return null;
}

/** Libellé pays affiché quand une fiche n’a pas de ville. */
export function discoveryDefaultCountryLabel(
  market: 'BE' | 'NL' | null,
  labels: { home: string; be: string; nl: string }
): string {
  if (market === 'BE') return labels.be;
  if (market === 'NL') return labels.nl;
  return labels.home;
}

function argsHasServiceTypeId(args: BuildProsDiscoveryParamsArgs): boolean {
  if (
    args.selectedServiceTypeId !== null &&
    args.selectedServiceTypeId !== undefined &&
    String(args.selectedServiceTypeId).trim() !== ''
  ) {
    return true;
  }
  const name = args.selectedServiceTypeName?.trim();
  return Boolean(name && /^\d+$/.test(name));
}

/** La recherche inclut un filtre par type de service (service_types.id), URL compatible discovery. */
export function discoverySearchHasServiceTypeFilter(search: string): boolean {
  const p = new URLSearchParams(search);
  if (p.get('serviceTypeId') || p.get('service_type_id')) return true;
  const st = p.get('serviceType') || p.get('service');
  return st != null && /^\d+$/.test(st.trim());
}

/** Recherche filtrée par métier (?profession=slug). */
export function discoverySearchHasProfessionFilter(search: string): boolean {
  return Boolean(new URLSearchParams(search).get('profession'));
}

function normalizeForMatch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/** Déduit un slug métier quand l’utilisateur a tapé un libellé reconnu sans cliquer une suggestion. */
export function inferProfessionSlugFromText(text: string): string | null {
  const intent = resolveSpeciesSearchIntent(text);
  if (intent.professionSlugHint) return intent.professionSlugHint;

  const q = (intent.textForProfession || text).trim();
  if (!q) return null;
  const matches = findMatchingProfessionsSimple(q);
  if (matches.length === 1) return matches[0].slug ?? null;
  const normalized = normalizeForMatch(q);
  const exact = matches.find(m => normalizeForMatch(m.name) === normalized);
  return exact?.slug ?? null;
}

export const DISCOVERY_SEARCH_INCOMPLETE_MESSAGE =
  'Choisissez un métier ou une prestation dans les suggestions pour lancer la recherche.';

/**
 * Retourne les query params discovery (moteur GET /search/pros).
 */
export function buildProsDiscoveryParams(args: BuildProsDiscoveryParamsArgs): URLSearchParams {
  const params = new URLSearchParams();
  const q = args.searchQuoi.trim();
  const ou = args.searchOu.trim();
  const speciesIntent = q ? resolveSpeciesSearchIntent(q) : null;

  if (args.selectedProfessionSlug) {
    params.set('profession', args.selectedProfessionSlug);
  }

  if (args.selectedServiceTypeName?.trim()) {
    params.set('serviceType', args.selectedServiceTypeName.trim());
    if (args.selectedServiceTypeId !== null && args.selectedServiceTypeId !== undefined) {
      params.set('serviceTypeId', String(args.selectedServiceTypeId));
    }
  } else if (args.selectedServiceTypeId !== null && args.selectedServiceTypeId !== undefined) {
    params.set('serviceTypeId', String(args.selectedServiceTypeId));
  } else if (!args.selectedProfessionSlug && q && !isDefaultProfessionLabel(q)) {
    const inferred = inferProfessionSlugFromText(q);
    if (inferred) {
      params.set('profession', inferred);
    }
  }

  let speciesId: number | null = null;
  if (
    args.selectedSpeciesId !== null &&
    args.selectedSpeciesId !== undefined &&
    String(args.selectedSpeciesId).trim() !== ''
  ) {
    const sid = parseInt(String(args.selectedSpeciesId), 10);
    if (Number.isFinite(sid) && sid > 0) speciesId = sid;
  } else if (speciesIntent?.speciesId) {
    speciesId = speciesIntent.speciesId;
  }
  if (speciesId != null) {
    params.set('speciesId', String(speciesId));
  }

  if (ou) {
    params.set('address', ou);
  }
  if (args.lat !== 0 && args.lon !== 0) {
    params.set('lat', String(args.lat));
    params.set('lon', String(args.lon));
  }

  const existingSort = new URLSearchParams(args.currentLocationSearch).get('sort');
  let nextSort = 'relevance';
  if (existingSort && (SORT_VALUES as readonly string[]).includes(existingSort)) {
    nextSort = existingSort;
  }
  const prevProf = new URLSearchParams(args.currentLocationSearch).get('profession');
  const allowPriceSort =
    (argsHasServiceTypeId(args) ||
      discoverySearchHasServiceTypeFilter(args.currentLocationSearch)) &&
    !args.selectedProfessionSlug &&
    !prevProf;
  if (nextSort === 'price' && !allowPriceSort) {
    nextSort = 'relevance';
  }
  params.set('sort', nextSort);

  const prevParams = new URLSearchParams(args.currentLocationSearch);
  const existingRadius = prevParams.get('radius');
  const rNum = existingRadius != null ? parseInt(existingRadius, 10) : NaN;
  if (Number.isFinite(rNum) && (DISCOVERY_RADIUS_KM_OPTIONS as readonly number[]).includes(rNum)) {
    params.set('radius', String(rNum));
  }

  // Filtre pays : arg explicite > URL courante. FR non émis (régression).
  const countryParam =
    discoveryCountryQueryParam(args.country) ||
    discoveryCountryQueryParam(prevParams.get('country'));
  if (countryParam) {
    params.set('country', countryParam);
  }

  if (discoveryMfecFilterActive(prevParams)) {
    setDiscoveryMfecFilter(params, true);
  }

  return params;
}

/** Destination SPA après une recherche (résultats filtrés, pas d’annuaire global). */
export function buildDiscoveryNavigationPath(params: URLSearchParams): string {
  if (!discoverySearchHasMinimumFilters(params)) {
    return BOOKING_LANDING_PATH;
  }
  return `${SEARCH_RESULTS_PATH}?${params.toString()}`;
}

/** Chemin vitrine `/services/{slug}` pour un slug API `profession`. */
export function servicePathForProfessionSlug(
  professionSlug: string | null | undefined
): string | null {
  if (!professionSlug) return null;
  const entry = Object.values(METIERS_DATA).find(m => m.profession === professionSlug);
  return entry ? `/services/${entry.slug}` : null;
}

/** Lien vers la recherche discovery pré-remplie (métier + ville). */
export function buildCityMetierSearchHref(args: {
  profession: string;
  cityName: string;
  lat?: number;
  lon?: number;
}): string {
  const params = new URLSearchParams();
  params.set('profession', args.profession);
  params.set('address', args.cityName);
  const hasCoords =
    args.lat !== undefined &&
    args.lon !== undefined &&
    Number.isFinite(args.lat) &&
    Number.isFinite(args.lon) &&
    args.lat !== 0 &&
    args.lon !== 0;
  if (hasCoords) {
    params.set('lat', String(args.lat));
    params.set('lon', String(args.lon));
  }
  params.set('sort', 'relevance');
  params.set('radius', String(DEFAULT_DISCOVERY_RADIUS_KM));
  return `${SEARCH_RESULTS_PATH}?${params.toString()}`;
}

/**
 * Requêtes Nominatim à partir d’un libellé Google Places (« Lille, Nord, France »).
 * La ville (1er segment) d’abord : plus fiable que la description complète.
 */
export function geocodeQueryCandidates(city: string): string[] {
  const trimmed = city.trim();
  if (!trimmed) return [];
  const first = trimmed.split(',')[0].trim();
  if (first && first.toLowerCase() !== trimmed.toLowerCase()) {
    return [first, trimmed];
  }
  return [trimmed];
}

/** Géocodage léger via Nominatim pour une ville saisie à la main.
 * `countryCode` : un code ISO ou plusieurs séparés par des virgules (ex. fr,be,nl).
 */
export async function geocodeCity(
  city: string,
  countryCode: string | string[] = 'fr',
): Promise<[number, number]> {
  const cc = (Array.isArray(countryCode) ? countryCode.join(',') : countryCode)
    .toLowerCase()
    .replace(/\s+/g, '');
  for (const query of geocodeQueryCandidates(city)) {
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=${cc}`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0)) {
          return [lat, lon];
        }
      }
    } catch {
      /* ignore and try next candidate */
    }
  }
  return [0, 0];
}

export async function geocodeCityFrance(city: string): Promise<[number, number]> {
  return geocodeCity(city, 'fr');
}

/** Métiers proches (slugs API) pour une landing métier — exclut ostéo. */
export function mapRelatedProfessionSlugs(
  landingSlug: string | null | undefined
): Array<{ profession: string; label: string }> {
  if (!landingSlug) return [];
  const entry = METIERS_DATA[landingSlug];
  const related = entry?.relatedMetiers ?? [];
  const seen = new Set<string>();
  const out: Array<{ profession: string; label: string }> = [];
  for (const slug of related) {
    const metier = METIERS_DATA[slug];
    const profession = metier?.profession?.trim();
    if (!profession || profession === 'osteopath') continue;
    if (profession === landingSlug) continue;
    if (seen.has(profession)) continue;
    seen.add(profession);
    out.push({ profession, label: metier?.singular?.trim() || profession });
  }
  return out;
}

/** Slug API (`trainers`) → clé SEO de METIERS_DATA (`educateur-canin`). */
export function seoSlugFromProfession(professionSlug: string | null | undefined): string | null {
  if (!professionSlug) return null;
  const entry = Object.values(METIERS_DATA).find(m => m.profession === professionSlug);
  return entry?.slug ?? null;
}

/** Prochain rayon proposé quand la recherche ask ne ramène personne. */
export function nextAskExpandRadius(currentKm: number): number | null {
  if (currentKm <= DEFAULT_DISCOVERY_RADIUS_KM) return 50;
  if (currentKm < 100) return 100;
  return null;
}

/** Feature gate hero « Décrivez-nous la situation » (Booking Ask). */
export function isBookingAskAvailable(args: {
  bookingAskEnabled: boolean;
  discoveryEnabled: boolean;
  geoCountry?: string | null;
  hasInitialProfession?: boolean;
}): boolean {
  if (!args.bookingAskEnabled || !args.discoveryEnabled) return false;
  if (args.hasInitialProfession) return false;
  const cc = (args.geoCountry || '').trim().toUpperCase();
  if (cc === 'BE' || cc === 'NL') return false;
  return true;
}
